import { useCallback, useEffect, useState } from "react";

export interface CanvasLink {
  id: string;
  portalId: string;
  sourceNodeId: string;
  sourceHandle: string | null;
  targetNodeId: string;
  targetHandle: string | null;
  label: string | null;
}

interface ApiLink {
  id: string;
  portalId: string;
  sourceNodeId: string;
  sourceHandle: string | null;
  targetNodeId: string;
  targetHandle: string | null;
  label: string | null;
}

interface ListResponse {
  links: ApiLink[];
}

async function getJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const b = body as { error?: string; message?: string };
    throw new Error(b.error ?? b.message ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export interface AddLinkArgs {
  sourceNodeId: string;
  sourceHandle: string | null;
  targetNodeId: string;
  targetHandle: string | null;
}

export function useCanvasLinks(portalId: string | null) {
  const [links, setLinks] = useState<CanvasLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!portalId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getJson<ListResponse>(`/api/canvas-links/${portalId}`);
      setLinks(data.links);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [portalId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const add = useCallback(
    async (args: AddLinkArgs) => {
      if (!portalId) return;
      const created = await getJson<CanvasLink>(
        `/api/canvas-links/${portalId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...args, label: null }),
        },
      );
      setLinks((curr) => [...curr, created]);
    },
    [portalId],
  );

  const updateLabel = useCallback(
    async (id: string, label: string | null) => {
      if (!portalId) return;
      setLinks((curr) => curr.map((l) => (l.id === id ? { ...l, label } : l)));
      await getJson(`/api/canvas-links/${portalId}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label }),
      });
    },
    [portalId],
  );

  const remove = useCallback(
    async (id: string) => {
      if (!portalId) return;
      setLinks((curr) => curr.filter((l) => l.id !== id));
      await getJson(`/api/canvas-links/${portalId}/${id}`, {
        method: "DELETE",
      });
    },
    [portalId],
  );

  return { links, loading, error, reload, add, updateLabel, remove };
}
